<template>
  <v-dialog v-model="dialog" persistent :max-width="maxWidth">
    <template #activator="data">
      <span v-if="!hideButton">
        <v-btn
          :id="buttonId"
          :class="buttonClass"
          :color="color"
          :small="smallButton"
          :disabled="disabled"
          @click.stop="openDialog"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attr }">
              <v-icon class="mr-1" v-bind="attr" v-on="on">
                {{ buttonIcon }}
              </v-icon>
            </template>
            <span>{{ buttonToolTipText }}</span>
          </v-tooltip>
          <span v-if="!hideButtonText">
            {{ buttonText }}
          </span>
        </v-btn>
      </span>
    </template>
    <v-card>
      <v-card-title>
        <v-layout>
          <v-flex>
            <h4 v-if="!hideHeaderTitle">{{ headerTitle }}</h4>
          </v-flex>
          <div v-if="!confirmDisabled && !hideCloseIcon">
            <v-icon class="c-pointer" @click="close"> close </v-icon>
          </div>
        </v-layout>
      </v-card-title>
      <v-card-text v-if="$slots.default !== undefined">
        <p class="mb-0 break-word">
          <slot />
        </p>
        <v-form v-if="typeToConfirm" ref="form">
          <v-text-field
            v-model="confirmInputValue"
            :rules="[
              (v) =>
                v === typeToConfirm || `Type '${typeToConfirm}' to proceed`,
            ]"
          />
        </v-form>
        <p v-if="confirmWaitingToEnable">
          <small v-if="confirmWaitingToEnable">
            Wait {{ secondsToEnable }} second(s) to proceed
          </small>
        </p>
      </v-card-text>
      <v-card-text v-else-if="!noBody && !customText">
        <slot name="text-body">
          <p>
            <b>"{{ title }}"</b> will be {{ buttonText.toLowerCase() }}d, along
            with all related data.
          </p>
        </slot>
        <slot name="description" />
      </v-card-text>
      <v-card-text v-else-if="!noBody && customText">
        <slot name="custom-text-body">
          <p class="mb-0 break-word">{{ customText }}</p>
        </slot>
        <slot name="description" />
      </v-card-text>
      <v-card-actions>
        <slot name="card-actions">
          <v-btn
            id="ConfirmDelete"
            class="sb_btn_solid"
            :color="color"
            :disabled="confirmDisabled"
            :loading="confirmWaitingToEnable"
            @click="confirm"
          >
            {{ confirmButtonText }}
          </v-btn>
          <v-btn
            v-if="!hideCancelButton"
            id="CancelDelete"
            class="sb_btn_solid"
            outline
            :color="color"
            @click="cancel"
          >
            {{ cancelButtonText }}
          </v-btn>
        </slot>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * This modal is shown to confirm that a user wants to delete a campaign
 */
export default {
  name: 'DeleteConfirmModal',
  props: {
    title: {
      type: String,
      required: true,
    },
    smallButton: {
      type: Boolean,
    },
    buttonClass: {
      type: String,
      default() {
        return 'sb_btn_solid';
      },
    },
    buttonText: {
      type: String,
      default() {
        return 'Delete';
      },
    },
    buttonToolTipText: {
      type: String,
      default() {
        return 'Archive';
      },
    },
    confirmButtonText: {
      type: String,
      default() {
        return 'Confirm';
      },
    },
    cancelButtonText: {
      type: String,
      default() {
        return 'Cancel';
      },
    },
    buttonId: {
      type: String,
      default() {
        return 'DeleteBtn';
      },
    },
    buttonIcon: {
      type: String,
      default() {
        return this.SbIcons.remove.default;
      },
    },
    noBody: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    hideHeaderTitle: {
      type: Boolean,
    },
    hideCloseIcon: {
      type: Boolean,
    },
    headerTitle: {
      type: String,
      default() {
        return 'Are you sure?';
      },
    },
    color: {
      type: String,
      default() {
        return 'error';
      },
    },
    customText: {
      type: String,
      default() {
        return undefined;
      },
    },
    hideButtonText: {
      type: Boolean,
    },
    hideButton: {
      type: Boolean,
    },
    hideCancelButton: {
      type: Boolean,
    },
    maxWidth: {
      type: String,
      default() {
        return '400px';
      },
    },
    secondsToEnable: {
      type: Number,
      default() {
        return 0;
      },
    },
    /**
     * If this is set, the user will have to type this value to confirm
     */
    typeToConfirm: {
      type: String,
      default() {
        return undefined;
      },
    },
  },
  data() {
    return {
      dialog: false,
      confirmWaitingToEnable: false,
      confirmInputValue: '',
    };
  },
  computed: {
    confirmDisabled() {
      let needsTypedConfirmation = false;
      if (this.typeToConfirm) {
        needsTypedConfirmation = this.confirmInputValue !== this.typeToConfirm;
      }
      return this.confirmWaitingToEnable || needsTypedConfirmation;
    },
  },
  methods: {
    openDialog() {
      this.dialog = true;

      this.confirmWaitingToEnable = true;
      setTimeout(() => {
        this.confirmWaitingToEnable = false;
      }, this.secondsToEnable * 1000);

      if (this.$refs.form) {
        this.$refs.form.validate();
      }
    },
    closeDialog() {
      this.dialog = false;
      this.confirmInputValue = '';
    },
    confirm() {
      this.$emit('confirm');
      this.closeDialog();
    },
    close() {
      this.closeDialog();
    },
    cancel() {
      this.$emit('cancel');
      this.closeDialog();
    },
  },
};
</script>
